import { useCallback, useMemo, useState } from "react";
import { AuthUser } from "../../models/User";
import { Channel, mergeChannelConnectionFallback, normalizeChannelConnection, normalizeChannelConnections } from "../../models/Channel";
import { SavedSticker } from "../../services/savedStickerServices";
import { clearSessionAccessToken } from "../../services/sessionToken";

export function useUser() {
  const [ user, setUser ] = useState<AuthUser | null>( null );

  const changeChannelStatus = useCallback(( channel: Channel ) => {
    setUser(( user ) => {
      if( !user ) return user;

      const currentDefault = user.organization.channel;
      const currentConnections = mergeChannelConnectionFallback(user.organization.channelConnections ?? [], currentDefault);
      const normalizedChannel = normalizeChannelConnection({
        ...currentDefault,
        ...channel,
        id: channel.id || currentDefault?.id || "",
      });
      const hasExistingConnection = currentConnections.some((connection) => connection.id === normalizedChannel.id);
      const channelConnections = hasExistingConnection
        ? currentConnections.map((connection) => connection.id === normalizedChannel.id ? { ...connection, ...normalizedChannel } : connection)
        : normalizedChannel.id ? [ ...currentConnections, normalizedChannel ] : currentConnections;
      const nextDefault = !currentDefault?.id || normalizedChannel.id === currentDefault.id || normalizedChannel.isDefault
        ? { ...currentDefault, ...normalizedChannel }
        : currentDefault;

      const newUser = {
        ...user,
        organization: {
          ...user.organization,
          channel: nextDefault,
          channelConnections,
        },
      };

      return { ...newUser } as AuthUser;
    });
  }, []);

  const setChannelConnections = useCallback(( connections: Array<Channel> ) => {
    setUser(( user ) => {
      if( !user ) return user;

      const channelConnections = mergeChannelConnectionFallback(normalizeChannelConnections(connections), user.organization.channel);
      const defaultChannel = channelConnections.find((connection) => connection.isDefault)
        ?? channelConnections.find((connection) => connection.id === user.organization.channel?.id)
        ?? user.organization.channel;
      const newUser = {
        ...user,
        organization: {
          ...user.organization,
          channel: defaultChannel,
          channelConnections,
        },
      };

      return { ...newUser } as AuthUser;
    });
  }, []);

  const login = useCallback(( user: AuthUser ) => {
    setUser({
      ...user,
      savedStickers: user.savedStickers ?? [],
    });
  }, []);

  const upsertSavedSticker = useCallback(( sticker: SavedSticker ) => {
    setUser(( user ) => {
      if( !user ) return user;

      return {
        ...user,
        savedStickers: [ sticker, ...( user.savedStickers ?? [] ).filter(( item ) => item.id !== sticker.id ) ].slice( 0, 48 ),
      };
    });
  }, []);

  const removeSavedSticker = useCallback(( stickerId: string ) => {
    setUser(( user ) => {
      if( !user ) return user;

      return {
        ...user,
        savedStickers: ( user.savedStickers ?? [] ).filter(( item ) => item.id !== stickerId ),
      };
    });
  }, []);

  const logout = useCallback(() => {
    clearSessionAccessToken();
    setUser( null );
  }, []);

  return useMemo(() => ({
    user,
    login,
    logout,
    upsertSavedSticker,
    removeSavedSticker,
    changeChannelStatus,
    setChannelConnections,
  }), [
    user,
    login,
    logout,
    upsertSavedSticker,
    removeSavedSticker,
    changeChannelStatus,
    setChannelConnections,
  ]);
};
